import React, { useState, useCallback, useEffect } from "react";
import { Box, Button, LinearProgress, Paper, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import UploadStage from "./ResourseUploadStage";
import AnalyzingStage, { STEPS } from "./ResourceAnalysingStage";
import ResultsStage from "./ResourceResultStage";
import { useDispatch } from "react-redux";
import { invokeGetService, invokePostService } from "../../../store/global/actions";
import config from "../../../config";
import { getDetailsFromUrl } from "../../../services/blueprint";

const BORDER = "#dde0ee";

const useStyles = makeStyles((theme) => ({
  pane: {
    background: "#fff",
    padding: "20px 22px",
    display: "flex",
    flexDirection: "column",
    gap: 14,
    minHeight: 460,
    width: '100%',
    boxSizing: "border-box",
    position: 'relative',
    overflowY: 'auto'
  },
  loaderContainer: {
    top: 0,
    left: 0,
    right: 0,
    zIndex: 10,
    height: 4,
    position: 'absolute'
  },
  linearProgress: {
    height: 3,
    backgroundColor: 'transparent',
    '& .MuiLinearProgress-barColorPrimary': {
      backgroundColor: '#3B82F6',
    },
  },
  loaderGlow: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 20,
    background: 'linear-gradient(to bottom, rgba(0, 98, 255, 0.1), transparent)',
    pointerEvents: 'none',
  },
  emptyContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
  },
  actionCard: {
    maxWidth: '100%',
    width: '100%',
    padding: theme.spacing(3),
    backgroundColor: '#f8fafc',
    borderRadius: 16,
    border: '1px dashed #e2e8f0',
    boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
  },
  aiButton: {
    marginTop: theme.spacing(3),
    backgroundColor: '#34445c', // Your brand slate
    color: '#fff',
    fontWeight: 700,
    textTransform: 'none',
    padding: '10px 24px',
    borderRadius: 8,
    '&:hover': {
      backgroundColor: '#1e293b',
    },
  },
  divider: {
    margin: theme.spacing(2, 0),
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    color: '#94a3b8',
    '&::before, &::after': {
      content: '""',
      flex: 1,
      height: 1,
      backgroundColor: '#e2e8f0',
    },
    '& span': {
      padding: '0 10px',
      fontSize: '0.7rem',
      fontWeight: 700,
      textTransform: 'uppercase',
    }
  }
}));

const DEFAULT_FILES = [];

export default function HyperbloXUploadPane({setSummaryExist, setActiveStep, setAutoGenerate, clusterletsList}) {
  const cls = useStyles();
  const dispatch = useDispatch();
  const {blueprintId} = getDetailsFromUrl()

  const [stage, setStage] = useState("upload"); // "upload" | "analyzing" | "results"
  const [files, setFiles] = useState([]);
  const [stepStates, setStepStates] = useState(STEPS.map(() => "pending")); // "pending"|"active"|"done"
  const [stepProgress, setStepProgress] = useState(STEPS.map(() => 0));
  const [summary, setSummary] = useState({})

  useEffect(() => {
    setStage('loading')
    poll()
  },[])

  // ── File management ──────────────────────────────────────────────────────────
  const addFiles = useCallback((fileList) => {
    setFiles(fileList);
  }, []);

  const removeFile = (index) =>
    setFiles((prev) => prev.filter((_, i) => i !== index));

  const saveFile = async () => {
    const formData = new FormData();
    if(files[0]){
      formData.append('file', files[0]);
    }
    
    const response = await fetch(`${config.apiV2Url}ai/file-analysis?blueprint=${blueprintId}`, {
      method: "POST",
      body: formData,
      headers: {
        'Authorization': localStorage.getItem('token')
      }
    })
    const data = await response.json()
    console.log(data,'this is data')
    if(data.success){
      runAnalysis();
    }
  }

  const poll = async (advanceStep) => {
    try {
        const response = await dispatch(invokeGetService('llmFileSummary', {}));

        if(advanceStep) advanceStep();

        if (response?.status === "processing") {
            setTimeout(() => poll(advanceStep), 5000);
        } else if (response?.status === "processed") {
            setStepStates(STEPS.map(() => "done"));
            setSummary(response.summary)
            setSummaryExist(true)
            setFiles(response?.files?.map(file => ({name: file})))
            setTimeout(() => setStage("results"), 350);
        }else{
          setStage('upload')
        }
    } catch (err) {
        setStage('upload')
        console.error("[runAnalysis] polling error:", err);
    }
};

  // ── Analysis runner ──────────────────────────────────────────────────────────
  const runAnalysis = async () => {
    setStage("analyzing");
    setStepStates(STEPS.map(() => "pending"));

    let currentStep = 0;

    const advanceStep = () => {
        if (currentStep < STEPS.length) {
            setStepStates((prev) =>
                prev.map((s, idx) => {
                    if (idx < currentStep) return "done";
                    if (idx === currentStep) return "active";
                    return s;
                })
            );
            currentStep++;
        }
    };

    poll(advanceStep);
  };

  // ── Reset ────────────────────────────────────────────────────────────────────
  const reset = () => {
    setFiles([]);
    setStage("upload");
    setStepStates(STEPS.map(() => "pending"));
    setStepProgress(STEPS.map(() => 0));
  };


  useEffect(() => {
    if(files?.length && files[0]?.type && stage === "upload"){
      setStage('uploading')
      saveFile();
    }
  }, [files, stage])


  return (
    <Paper elevation={0} className={cls.pane}>
      {
        stage === "loading" && (
          <Box className={cls.loaderContainer}>
            <LinearProgress className={cls.linearProgress} />
            <div className={cls.loaderGlow} />
          </Box>
        )
      }
      {stage === "upload" && (
        <UploadStage
          files={files}
          onFilesAdded={addFiles}
          onRemove={removeFile}
          // onAnalyze={runAnalysis}
        />
      )}

      {(stage === "analyzing" || stage === "uploading") && (
        <AnalyzingStage
          stepStates={stepStates}
          stepProgress={stepProgress}
          fileName={files[files.length - 1]?.name}
          analyzing={stage === "analyzing"}
          uploading={stage === "uploading"}
        />
      )}

      {stage === "results" && (
        <ResultsStage
          lastFileName={files[files.length - 1]?.name ?? "document.pdf"}
          onReset={reset}
          summary={summary}
          setActiveStep={setActiveStep}
          setAutoGenerate={setAutoGenerate}
          clusterletsList={clusterletsList}
        />
      )}
    </Paper>
  );
}